import { ULTIMATE } from '../../../config/constants.js';

/**
 * Owns the charge meter and the active window for the chosen champion's
 * ultimate (data/ultimates.js). One instance per run, ticked from
 * Game._update; the def supplies the actual behavior through its
 * onStart/onTick/onEnd hooks, so adding a champion never touches this file.
 *
 * Charges on a constant clock (ULTIMATE.passiveChargePerSecond, a full bar
 * every 50s) plus a small graze bonus, at the prices in ULTIMATE
 * (config/constants.js), scaled by the graze chain (§D5) when one is wired in,
 * plus absorbed toned bullets in Act II's polarity fights (§D7, priced by the
 * resonance chain that reports them).
 * Damage dealt does NOT charge the meter — the cycle
 * is meant to read the same in every fight rather than tracking DPS or the
 * HP pool currently on the field.
 *
 * Since the Awakening merge this is the game's only charged button (Space):
 * activating also puts the player into the generic combat surge that used to
 * be Awakening's (Player.isSurging), on top of the def's own effect.
 */
export class UltimateSystem {
  constructor(eventBus, { player, combat, ultimate = null, grazeChain = null } = {}) {
    this.eventBus = eventBus;
    this.player = player;
    this.combat = combat;
    this.def = ultimate;
    // BULLET-HELL.md §D5 — grazes pay `chargePerGraze × the chain multiplier`.
    // Optional: with no chain injected a graze is worth its flat rate, which
    // is what every test and any future headless harness gets.
    this.grazeChain = grazeChain;
    this.charge = ultimate ? ULTIMATE.startingCharge : 0;
    this.isActive = false;
    this._remaining = 0;
    this._elapsed = 0;
    // Per-activation scratch for the def's hooks (spiral angles, retrigger
    // timers, event unsubscribes) — replaced wholesale on every activation so
    // nothing can leak between casts.
    this.state = {};

    if (!this.def) return;
    // Reads the chain's CURRENT multiplier, which is exact rather than one
    // graze stale: Game builds the GrazeChain before this system, so its own
    // `bullet:grazed` listener has already counted this graze by the time
    // this one runs.
    eventBus.on('bullet:grazed', () => {
      this.addCharge(ULTIMATE.chargePerGraze * (this.grazeChain?.multiplier ?? 1));
    });
    // Absorbing a matched-tone bullet (BULLET-HELL.md §D7). Listens to
    // `polarity:absorbed` rather than the raw `bullet:absorbed` precisely so the
    // multiplier is the one Resonance just counted — the same ordering argument
    // as the graze listener above, expressed as an event rather than as
    // construction order, because Resonance is the thing that knows when an
    // absorb has been priced.
    eventBus.on('polarity:absorbed', ({ multiplier = 1 } = {}) => {
      this.addCharge(ULTIMATE.chargePerAbsorb * multiplier);
    });
    // Reckless Pact (data/levelUpPool.js): a landed hit bleeds the bar. Only
    // fires on damage that actually stuck — Player emits player:damaged after
    // i-frames, dodges and Aegis plates have all bailed out.
    eventBus.on('player:damaged', () => {
      const drain = this.player?.ultimateDrainOnHit ?? 0;
      if (drain > 0 && !this.isActive) this.charge = Math.max(0, this.charge - drain);
    });
  }

  /** 0..1 — drives the HUD meter. Always 0 for a run with no ultimate def. */
  get chargeFraction() {
    return this.def ? this.charge : 0;
  }

  get isReady() {
    return !!this.def && !this.isActive && this.charge >= 1;
  }

  /** 0..1 of the active window still to run — drives the meter's drain-down while it's up. */
  get activeFraction() {
    const duration = (this.def?.duration ?? 0) * (this.player?.ultimateDurationMultiplier ?? 1);
    if (!this.isActive || !duration) return 0;
    return Math.max(0, this._remaining / duration);
  }

  addCharge(amount) {
    if (!this.def || this.isActive || amount <= 0) return;
    const before = this.charge;
    // Crescendo scales both sources alike (graze + the constant tick).
    this.charge = Math.min(1, this.charge + amount * (this.player?.ultimateChargeGainMultiplier ?? 1));
    // Edge-triggered on the fill crossing full, exactly like the Focus
    // meter's transform-ready cue — a full bar is easy to miss mid-dodge.
    if (before < 1 && this.charge >= 1) {
      this.eventBus.emit('ultimate:ready', { name: this.def.name, color: this.def.color });
    }
  }

  /**
   * Fires the ultimate if it's charged. Returns whether it went off, so the
   * caller can distinguish "not ready" from "spent" without reading state.
   */
  activate() {
    if (!this.isReady) return false;
    // Deathbomb (BULLET-HELL.md §D3). Resolved before anything else this
    // activation does: a def's onStart can read or write HP (Requiem heals,
    // Bastion mitigates), and all of that should see the board as it is with
    // the hit refunded, not with it still pending. Nothing here is gated on
    // whether a hit was caught — the meter is spent identically either way,
    // which is what keeps a deathbomb a *cost* rather than a discount.
    this.player?.voidPendingHit?.();
    this.charge = 0;
    this.isActive = true;
    this._elapsed = 0;
    // Crescendo trades a faster fill for a shorter window.
    this._remaining = (this.def.duration ?? 0) * (this.player?.ultimateDurationMultiplier ?? 1);
    this.state = {};
    this.eventBus.emit('ultimate:activated', {
      id: this.def.id, name: this.def.name, color: this.def.color, x: this.player.x, y: this.player.y,
    });
    this.def.onStart?.(this._context());
    // A zero-duration (purely instant) def would otherwise sit "active"
    // forever with nothing to tick it down.
    if (this._remaining <= 0) this._end();
    return true;
  }

  update(dt) {
    if (!this.def) return;
    if (this.isActive) {
      this._elapsed += dt;
      this.def.onTick?.(this._context(), dt);
      this._remaining -= dt;
      if (this._remaining <= 0) this._end();
      return;
    }
    // The whole fill: a constant 50s from empty to full, identical in every
    // fight. Grazes are the only thing that can shorten it.
    this.addCharge(ULTIMATE.passiveChargePerSecond * dt);
  }

  /**
   * Closes an open window early — called when the run ends, so a def can't
   * strand a flag it flipped on (Zero Hour's time scale, Skybreaker's free
   * dash) past the fight it was cast in.
   */
  cancel() {
    if (this.isActive) this._end();
  }

  _end() {
    this.isActive = false;
    this._remaining = 0;
    this.def.onEnd?.(this._context());
    this.eventBus.emit('ultimate:ended', { id: this.def.id, name: this.def.name, color: this.def.color });
  }

  _context() {
    return {
      player: this.player,
      combat: this.combat,
      eventBus: this.eventBus,
      state: this.state,
      elapsed: this._elapsed,
      duration: (this.def.duration ?? 0) * (this.player?.ultimateDurationMultiplier ?? 1),
    };
  }
}
